import type { ConsentChoices } from './consent-signals';

import {
  areAllChoicesAnswered,
  CURRENT_CONSENT_VERSION,
} from './consent-signals';

/**
 * What the consent prompt should render, if anything.
 *
 * `hidden` is not an opt-out. It means "defer to the cookie": consent stays at
 * whatever `rio_consent` says, and deny-by-default when the cookie is absent.
 * Nothing is written on suppression — an unanswered prompt must never be
 * recorded as a decision.
 */
export type ConsentPromptMode = 'hidden' | 'full' | 'compact';

export type ConsentPromptInput = {
  /**
   * Whether any consent record was resolved for this visitor at all — cookie,
   * legacy cookie or profile copy. This is deliberately separate from
   * {@link ConsentPromptInput.choices}: a visitor with no record has nothing
   * for the policy to contradict, while a record with gaps has a question
   * outstanding that no default may answer on the visitor's behalf.
   */
  hasStoredRecord: boolean;
  /**
   * Whether the region policy supplies consent for a visitor who has recorded
   * nothing, per `hasImpliedConsent`. It settles the prompt only in
   * combination with `hasStoredRecord: false`; it never silences a gap in a
   * record that exists.
   */
  hasImpliedConsent: boolean;
  /**
   * The answers this visitor has stored. The prompt exists to collect the
   * categories still `unset`, so a record that answers all of them settles
   * the question whatever version it was made under. Implied defaults are not
   * answers and must not be passed here.
   */
  choices: ConsentChoices;
  /**
   * True when those answers come from reconciling records that disagreed. The
   * denial that survived is a safe reading rather than the visitor's known
   * latest word, so the question is put to them even though every category
   * carries a value.
   */
  isReconciled: boolean;
  /**
   * Version from the stored consent (cookie first, profile as fallback), or
   * `null` when this browser/account has never recorded a choice.
   */
  storedConsentVersion: number | null;
  /**
   * True when apps/web is rendered in an iframe. The top-level page owns
   * consent: the marketing-site embed is same-site, so the `.respondent.io`
   * cookie the parent wrote is readable here and a second banner inside the
   * frame would be both redundant and unclickable in some embeds.
   */
  isEmbedded: boolean;
  /** Public screener apply routes get the compact treatment. */
  isPublicApplyRoute: boolean;
  /**
   * Local development. Consent is the developer's call, not something a local
   * harness (or an agent driving one) should be prompted for.
   */
  isLocalEnvironment: boolean;
};

export function decideConsentPrompt({
  hasStoredRecord,
  hasImpliedConsent,
  choices,
  isReconciled,
  storedConsentVersion,
  isEmbedded,
  isPublicApplyRoute,
  isLocalEnvironment,
}: ConsentPromptInput): ConsentPromptMode {
  if (isEmbedded) return 'hidden';
  if (isLocalEnvironment) return 'hidden';
  if (!hasStoredRecord && hasImpliedConsent) return 'hidden';
  if (!isReconciled && areAllChoicesAnswered(choices)) return 'hidden';
  if (
    storedConsentVersion !== null &&
    storedConsentVersion >= CURRENT_CONSENT_VERSION
  ) {
    return 'hidden';
  }
  return isPublicApplyRoute ? 'compact' : 'full';
}

/**
 * Iframe detection. A cross-origin embed throws on `window.top` access, which
 * is itself proof of being framed — so the throw path returns `true`.
 */
export function isEmbeddedInIframe(): boolean {
  if (globalThis.window === undefined) return false;
  try {
    return globalThis.window.self !== globalThis.window.top;
  } catch {
    return true;
  }
}
